<template>
    <div id="device-traited-data-by-device-metric-datatable-widget-component">
        <widget-wrapper-component
            :title="$t('device_traited_data_by_device_metric_datatable_widget_component.equipment_surveys')"
            :id-widget="`device-traited-data-by-device-metric-datatable-widget-${device?.device_number}`"
        >
            <template v-slot:default>
                <v-data-table
                    :headers="headers"
                    :items="device_traited_data"
                    :items-per-page="item_per_page"
                    :loading="fetching_dtd"
                    :page="page"
                    :server-items-length="total_rows"
                    loader-height="2"
                    v-on:update:page="handlePageUpdate"
                    v-on:update:items-per-page="handleItemsPerPageUpdate"
                >
                    <template v-slot:item.date_releve="{ item }">
                        <div class="text-no-wrap py-2">
                            {{ item.date_releve }}
                            <chips-last-statement :date="item.date_releve" />
                        </div>
                    </template>

                    <template v-for="(header, index) in headers.slice(1, headers.length)" v-slot:[`item.${header.value}`]="{ item }">
                        <div :key="index">
                            <template v-if="header.value === 'declencheur'">
                                {{ displayToHumanTrigger(item[header.value]) }}
                            </template>

                            <template v-else-if="header.value === 'erreur'">
                                {{ displayToHumanError(item[header.value], item['hauteur_liquide']) }}
                            </template>

                            <template v-else-if="header.value === 'batterie'">
                                <display-device-battery-level-component :battery="item[header.value]" />
                            </template>

                            <template v-else-if="header.value === 'quality_network'">
                                <span class="text-no-wrap">
                                    <template v-if="item[header.value] === null">
                                        <v-icon color="disabled">mdi-network-strength-4-cog</v-icon>
                                        {{ $t('global.no_data') }}
                                    </template>

                                    <template v-else-if="item[header.value] === 0 || item[header.value] === 1 || item[header.value] === 2">
                                        <v-icon color="error"> mdi-network-strength-1-alert </v-icon>
                                        {{ $t('global.bad') }}
                                    </template>

                                    <template v-else-if="item[header.value] === 3">
                                        <v-icon color="warning"> mdi-network-strength-3-alert </v-icon>
                                        {{ $t('global.medium') }}
                                    </template>

                                    <template v-else-if="item[header.value] === 4">
                                        <v-icon color="success">mdi-network-strength-3</v-icon>
                                        {{ $t('global.good') }}
                                    </template>

                                    <template v-else-if="item[header.value] === 5">
                                        <v-icon color="success">mdi-network-strength-4</v-icon>
                                        {{ $t('global.very_good') }}
                                    </template>
                                </span>
                            </template>

                            <template v-else>
                                {{ item[header.value] }}
                            </template>
                        </div>
                    </template>
                </v-data-table>
            </template>
        </widget-wrapper-component>
    </div>
</template>

<script>
import WidgetWrapperComponent from '@/components/global/wrapper/WidgetWrapperComponent.vue'
import DeviceRepository from '@/repositories/DeviceRepository'
import displayHuman from '@/mixin/displayHuman'
import ChipsLastStatement from '@/components/global/containment/ChipsLastStatement.vue'
import DisplayDeviceBatteryLevelComponent from '@/components/global/DisplayDeviceBatteryLevelComponent.vue'

export default {
    name: 'DeviceTraitedDataByDeviceMetricDatatableWidgetComponent',
    components: { DisplayDeviceBatteryLevelComponent, ChipsLastStatement, WidgetWrapperComponent },
    mixins: [displayHuman],
    props: {
        device: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            headers: [],
            device_traited_data: [],
            fetching_dtd: true,

            page: 1,
            item_per_page: 5,
            total_rows: 0,
        }
    },
    methods: {
        // The total is invariant across pagination: request it only when needed (initial load)
        // and reuse it otherwise to skip the expensive unbounded COUNT on the backend.
        fetchDeviceTraitedData(withTotal = false) {
            this.fetching_dtd = true
            this.device_traited_data = []
            DeviceRepository.getHistoricalByDevice(
                this.device.device_number,
                new URLSearchParams({
                    datatable: true,
                    limit: this.item_per_page,
                    offset: (this.page - 1) * this.item_per_page,
                    with_total: withTotal,
                })
            )
                .then((success) => {
                    if (success.data.data.data.total_rows !== undefined) {
                        this.total_rows = success.data.data.data.total_rows
                    }
                    this.device_traited_data = success.data.data.data.data
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.fetching_dtd = false
                })
        },

        buildHeadersForDeviceMetricsDatatable() {
            this.headers.push({
                text: 'Date relevé',
                value: 'date_releve',
                sortable: false,
            })

            this.device?.metrics.forEach((metric) => {
                if (!metric.display_only_for_admin && metric.visible) {
                    const label = `${metric.metric.column_name_dtd === 'quality_network' ? 'Qualité réseau' : metric.metric.label} ${
                        metric.metric.unit ? `(${metric.metric.unit})` : ''
                    }`

                    this.headers.push({
                        text: label,
                        value: metric.metric.column_name_dtd,
                        sortable: false,
                    })
                }
            })

            this.fetchDeviceTraitedData(true)
        },

        handlePageUpdate(page) {
            this.page = page
            this.fetchDeviceTraitedData()
        },

        handleItemsPerPageUpdate(item_per_page) {
            this.item_per_page = item_per_page
            this.fetchDeviceTraitedData()
        },
    },
    created() {
        this.buildHeadersForDeviceMetricsDatatable()
    },
}
</script>
